import React, { useState, useEffect } from 'react';
import { Card } from '../../../components/ui/card';
import { CreditCard, Edit, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../services/api';
import { getApiErrorMessage } from '../../../utils/apiErrors';

const AdminPlans = () => {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/billing/plans');
      setPlans(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load plans'));
    } finally {
      setLoading(false);
    }
  };

  const togglePlanStatus = async (planId, currentStatus) => {
    try {
      await api.patch(`/admin/billing/plans/${planId}`, { is_active: !currentStatus });
      toast.success(`Plan ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchPlans();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update plan'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plans Management</h1>
          <p className="text-gray-600 mt-1">Manage pricing and plan features</p>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.length === 0 ? (
          <div className="col-span-3 text-center py-12 text-gray-500">
            No plans configured yet
          </div>
        ) : (
          plans.map((plan) => {
            const limits = plan.limits || {};
            const features = plan.features || [];
            return (
              <Card key={plan.id} className="p-6 relative">
                {/* Status Badge */}
                <div className="absolute top-4 right-4">
                  <button
                    onClick={() => togglePlanStatus(plan.id, plan.is_active)}
                    className={`p-2 rounded-full ${
                      plan.is_active
                        ? 'bg-green-100 text-green-600 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={plan.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                  >
                    {plan.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>

                {/* Plan Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                    <h3 className="text-xl font-bold">{plan.name}</h3>
                  </div>
                  <div className="text-3xl font-bold">
                    ₹{plan.price_monthly || 0}
                    <span className="text-sm text-gray-600 font-normal">/month</span>
                  </div>
                  <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${
                    plan.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Features */}
                <div className="space-y-2 mb-6">
                  <h4 className="font-semibold text-sm text-gray-700">Features:</h4>
                  {features.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="text-sm text-gray-600">• {feature}</div>
                  ))}
                  {features.length > 3 && (
                    <div className="text-sm text-gray-500">+ {features.length - 3} more</div>
                  )}
                  {features.length === 0 && (
                    <div className="text-sm text-gray-400">No features listed</div>
                  )}
                </div>

                {/* Limits */}
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Limits:</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Staff:</span>
                      <span className="font-medium ml-1">{limits.staff_limit ?? 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Retailers:</span>
                      <span className="font-medium ml-1">{limits.retailers_limit ?? 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">GPS/Day:</span>
                      <span className="font-medium ml-1">{limits.gps_points_per_day ?? 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Storage:</span>
                      <span className="font-medium ml-1">{limits.storage_gb ?? 0} GB</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 pt-4 border-t">
                  <button
                    className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2"
                    onClick={() => toast.info('Edit functionality coming soon')}
                  >
                    <Edit className="w-4 h-4" />
                    Edit Plan
                  </button>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Info Card */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-2">Plan Management Tips</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Deactivating a plan will hide it from new signups, but won't affect existing subscribers</li>
          <li>• Price changes should be carefully communicated to existing customers</li>
          <li>• Limit increases can be applied immediately to existing subscribers</li>
        </ul>
      </Card>
    </div>
  );
};

export default AdminPlans;
